import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';
import {
  accountPushState,
  ensureSubscribed,
  getPushPermission,
  isPushSupported,
  PUSH_ACCOUNT,
  TAP_MESSAGE_TYPE,
  TAP_QUERY_PARAM,
  TAP_QUERY_VALUE,
} from '../utils/webPush';

const SYNCED_KEY_PREFIX = 'cellcog_push_synced:';

/**
 * App-level browser-push bookkeeping (CEL-2490, journey review 2026-09-09 gap 2 + measurement). Mounted once in
 * App.js AppContent (inside AuthProvider).
 * 1. Self-heal: account on + this browser's permission granted → make sure THIS browser is registered under THIS
 *    account (get-or-create the subscription, idempotent upsert on the server). Covers Chrome's quiet bubble granting
 *    after the card gave up, cleared site data, endpoint rotation (the worker re-subscribes, this registers it) and a
 *    shared machine whose previous account signed out. Once per tab session per user.
 * 2. Tap tracking: a page opened or navigated by a notification tap carries ?src=push (stripped here); a page the
 *    worker focused instead receives a postMessage. Both fire push_notification_tapped (js/cellcog/TRACKING.md).
 */
export default function usePushAccountSync() {
  const { userProfile } = useAuth();
  const userId = (userProfile && userProfile.id) || null;
  const account = accountPushState(userProfile);

  useEffect(() => {
    if (!userId || account !== PUSH_ACCOUNT.ON || !isPushSupported() || getPushPermission() !== 'granted') return;
    const key = SYNCED_KEY_PREFIX + userId;
    try {
      if (window.sessionStorage.getItem(key) === '1') return;
    } catch (e) {
      /* no storage: sync anyway */
    }
    ensureSubscribed()
      .then((ok) => {
        if (!ok) return;
        try { window.sessionStorage.setItem(key, '1'); } catch (e) { /* ignore */ }
      })
      .catch(() => {});
  }, [userId, account]);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get(TAP_QUERY_PARAM) === TAP_QUERY_VALUE) {
        trackEvent('push_notification_tapped', { kind: url.pathname.indexOf('/ai_employee/') === 0 ? 'employee' : 'chat', via: 'open' });
        url.searchParams.delete(TAP_QUERY_PARAM);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (e) {
      /* tracking must never break the app */
    }
    if (!isPushSupported()) return undefined;
    const onMessage = (event) => {
      const data = event && event.data;
      if (data && data.type === TAP_MESSAGE_TYPE) trackEvent('push_notification_tapped', { kind: data.kind || 'chat', via: 'focus' });
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    // The spec queues worker-to-page messages until the page opts in, and addEventListener alone does not opt in (only
    // onmessage or startMessages does): without this line a tap that FOCUSED an existing tab would never be counted.
    if (typeof navigator.serviceWorker.startMessages === 'function') navigator.serviceWorker.startMessages();
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);
}
